import React, { useEffect, useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import {
  FaCrown,
  FaDatabase,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import { MdOutlineTimer, MdWarningAmber } from "react-icons/md";
import { ShimmerThumbnail } from "react-shimmer-effects";
import DashboardCard from "../../../Components/Cards/DashboardCard";
import { useGetDashboardDetailsQuery } from "../../../Data/services/dashboardApi";
import { useGetEngagementSegmentsQuery } from "../../../Data/services/engagementApi";
import { ErrorToast } from "../../../Hooks/useToast";
import { ALERT_SEGMENT_KEYS, segmentLabel } from "../constants/labels";
import styles from "../styles/Dashboard.module.scss";
import { extractData, normalizeSegments } from "../utils";

export default function KpiStrip({ onSegmentSelect }) {
  const {
    data: dashboardRes,
    isLoading: dashLoading,
    isError: dashError,
    error: dashErr,
  } = useGetDashboardDetailsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: segmentsRes,
    isLoading: segmentsLoading,
    isError: segmentsError,
    error: segmentsErr,
  } = useGetEngagementSegmentsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (dashError) {
      ErrorToast(dashErr?.data?.message || "Failed to load dashboard KPIs");
    }
  }, [dashError, dashErr]);

  useEffect(() => {
    if (segmentsError) {
      ErrorToast(
        segmentsErr?.data?.message || "Failed to load engagement segments"
      );
    }
  }, [segmentsError, segmentsErr]);

  const dashboardData = extractData(dashboardRes) || {};
  const segments = normalizeSegments(extractData(segmentsRes));

  const kpiTiles = useMemo(
    () => [
      {
        key: "users",
        text: "Users",
        number: dashboardData.user_list ?? 0,
        bg1: "#0180F6",
        bg2: "#0180F6a1",
        icon: <FaUsers color="#0180F6" />,
      },
      {
        key: "posts",
        text: "Posts",
        number: dashboardData.post_counts ?? 0,
        bg1: "#0180F6",
        bg2: "#4BA3F7",
        icon: <FaDatabase color="#0180F6" />,
      },
      {
        key: "active",
        text: "Active",
        number: segments.active ?? 0,
        bg1: "#0B8F6E",
        bg2: "#3EBF8F",
        icon: <FaUserCheck color="#0B8F6E" />,
        onClick: () => onSegmentSelect?.("active"),
      },
      {
        key: "premium",
        text: "Premium",
        number: segments.premium ?? 0,
        bg1: "#0A6BCB",
        bg2: "#3D9AED",
        icon: <FaCrown color="#0A6BCB" />,
        onClick: () => onSegmentSelect?.("premium"),
      },
      {
        key: "trial_ending",
        text: "Trial ending",
        number: segments.trial_ending ?? 0,
        bg1: Number(segments.trial_ending) > 0 ? "#C45C00" : "#6c757d",
        bg2: Number(segments.trial_ending) > 0 ? "#E6A700" : "#9aa0a6",
        icon: <MdOutlineTimer color="#C45C00" />,
        alert: Number(segments.trial_ending) > 0,
        onClick: () => onSegmentSelect?.("trial_ending"),
      },
      {
        key: "sub_expiring",
        text: "Sub expiring",
        number: segments.sub_expiring ?? 0,
        bg1: Number(segments.sub_expiring) > 0 ? "#C62828" : "#6c757d",
        bg2: Number(segments.sub_expiring) > 0 ? "#FF5757" : "#9aa0a6",
        icon: <MdWarningAmber color="#C62828" />,
        alert: Number(segments.sub_expiring) > 0,
        onClick: () => onSegmentSelect?.("sub_expiring"),
      },
    ],
    [dashboardData.user_list, dashboardData.post_counts, segments, onSegmentSelect]
  );

  const showSkeleton =
    (dashLoading || segmentsLoading) && dashboardData.user_list == null;

  return (
    <>
      <Row className={`${styles.matchRow} w-100 mt-2 g-3`}>
        {showSkeleton
          ? Array.from({ length: 6 }).map((_, i) => (
              <Col key={i} xl={2} lg={4} md={4} sm={6}>
                <ShimmerThumbnail height={110} rounded />
              </Col>
            ))
          : kpiTiles.map((tile) => (
              <Col key={tile.key} xl={2} lg={4} md={4} sm={6}>
                <DashboardCard
                  compact
                  bg1={tile.bg1}
                  bg2={tile.bg2}
                  icon={tile.icon}
                  number={tile.number}
                  text={tile.text}
                  alert={tile.alert}
                  onClick={tile.onClick}
                />
              </Col>
            ))}
      </Row>

      {ALERT_SEGMENT_KEYS.some((k) => Number(segments[k]) > 0) ? (
        <div className={styles.chipRow}>
          {ALERT_SEGMENT_KEYS.map((key) =>
            Number(segments[key]) > 0 ? (
              <button
                key={key}
                type="button"
                className={`${styles.chip} ${styles.chipAlert}`}
                onClick={() => onSegmentSelect?.(key)}
              >
                {segmentLabel(key)}: {segments[key]}
              </button>
            ) : null
          )}
        </div>
      ) : null}
    </>
  );
}
